import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { Check, CheckCheck, Clock } from 'lucide-react';

interface Message {
  id: string;
  content: string;
  senderId: string;
  senderName: string;
  senderAvatar?: string;
  timestamp: Date;
  isMine: boolean;
  status?: 'sending' | 'sent' | 'delivered' | 'read';
  reactions?: Array<{
    emoji: string;
    count: number;
    userIds: string[];
  }>;
}

interface MessageBubbleProps {
  message: Message;
  isGroup?: boolean;
  showTimestamp?: boolean;
  theme?: {
    bubbleSent: string;
    bubbleReceived: string;
  };
}

export function MessageBubble({
  message,
  isGroup = false,
  showTimestamp = false,
  theme = {
    bubbleSent: 'bg-blue-500 text-white',
    bubbleReceived: 'bg-gray-100 dark:bg-gray-700 text-gray-900 dark:text-white'
  }
}: MessageBubbleProps) {
  const getStatusIcon = () => {
    switch (message.status) {
      case 'sending':
        return <Clock className="h-3 w-3 text-gray-400" />;
      case 'sent':
        return <Check className="h-3 w-3 text-gray-400" />;
      case 'delivered':
        return <CheckCheck className="h-3 w-3 text-gray-400" />;
      case 'read':
        return <CheckCheck className="h-3 w-3 text-blue-500" />;
      default:
        return null;
    }
  };

  return (
    <div className={cn(
      "flex gap-3",
      message.isMine ? "justify-end" : "justify-start"
    )}>
      {isGroup && !message.isMine && (
        <Avatar className="h-8 w-8 flex-shrink-0">
          <AvatarImage src={message.senderAvatar} />
          <AvatarFallback>{message.senderName.charAt(0)}</AvatarFallback>
        </Avatar>
      )}

      <div className={cn(
        "flex flex-col max-w-xs sm:max-w-md",
        message.isMine ? "items-end" : "items-start"
      )}>
        {isGroup && !message.isMine && showTimestamp && (
          <span className="text-xs text-gray-500 dark:text-gray-400 mb-1 ml-3">
            {message.senderName}
          </span>
        )}

        {showTimestamp && (
          <span className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            {format(message.timestamp, 'HH:mm', { locale: es })}
          </span>
        )}

        <div className={cn(
          "relative px-4 py-2 rounded-2xl break-words",
          message.isMine
            ? `${theme.bubbleSent} rounded-br-md`
            : `${theme.bubbleReceived} rounded-bl-md`
        )}>
          <p className="text-sm leading-relaxed">{message.content}</p>

          {message.isMine && (
            <div className="flex items-center justify-end mt-1 space-x-1">
              <span className="text-xs opacity-70">
                {format(message.timestamp, 'HH:mm', { locale: es })}
              </span>
              {getStatusIcon()}
            </div>
          )}

          {!message.isMine && (
            <span className="text-xs opacity-70 block mt-1">
              {format(message.timestamp, 'HH:mm', { locale: es })}
            </span>
          )}
        </div>

        {/* Reactions */}
        {message.reactions && message.reactions.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {message.reactions.map((reaction, index) => (
              <Badge
                key={index}
                variant="secondary"
                className={cn(
                  "text-xs px-2 py-1 h-auto",
                  message.isMine ? "bg-blue-100 text-blue-700" : "bg-gray-200 text-gray-700"
                )}
              >
                {reaction.emoji} {reaction.count}
              </Badge>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}